import clsx from "clsx";
import React from "react";
import Image from "next/image";
import { Images } from "@/app/assets/shared";

interface AiGradientBorderButtonProps {
  label?: string;
  className?: string;
  onClick?: () => void;
  dark?: boolean;
}

export default function AiGradientBorderButton({
  label = "Let's Build With AI",
  className,
  onClick,
  dark = true,
}: AiGradientBorderButtonProps) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "relative inline-flex items-center group rounded-full transition-all active:scale-95 overflow-visible",
        // Responsive width: 72px for icon-only, 210px for full text
        "w-[72px] 2xl:w-[240px] h-[52px]",
        className,
      )}
      aria-label={label}
      type="button"
    >
      {/* 1. SVG Glow - Now using percentage to prevent "cramping" */}
      <svg
        className="absolute inset-[-10px] w-[calc(100%+20px)] h-[calc(100%+20px)] pointer-events-none z-0 opacity-60"
        aria-hidden="true"
      >
        <defs>
          <filter id="glow-blur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feComposite in="blur" in2="SourceGraphic" operator="over" />
          </filter>
          <linearGradient
            id="ai-gradient-glow"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#FF2F4C" />
            <stop offset="53.89%" stopColor="#FCC21B" />
            <stop offset="104.76%" stopColor="#8723C1" />
          </linearGradient>
        </defs>
        <rect
          x="10"
          y="10"
          width="calc(100% - 20px)"
          height="calc(100% - 20px)"
          rx="26"
          stroke="url(#ai-gradient-glow)"
          strokeWidth="3"
          fill="none"
          filter="url(#glow-blur)"
        />
      </svg>

      {/* 2. Main Background Layer */}
      <div
        className="absolute inset-[1.5px] rounded-full z-10"
        style={{
          backdropFilter: "blur(6.3px)",
          WebkitBackdropFilter: "blur(6.3px)",
          boxShadow: "0px 0px 31.9px 0px rgba(255, 255, 255, 0.25) inset",
          background: "rgba(255, 255, 255, 0.02)",
        }}
      />

      {/* 3. Content Layer */}
      <div
        className={clsx(
          "relative z-30 flex items-center w-full h-full",
          "justify-center 2xl:justify-between px-2 2xl:pl-7 2xl:pr-2",
        )}
      >
        <span
          className={clsx(
            "text-[16px] font-semibold whitespace-nowrap transition-colors duration-200",
            "hidden 2xl:block",
            dark ? "text-white" : "text-black",
          )}
        >
          {label}
        </span>

        {/* The Star Pill Container */}
        <div
          className="flex items-center justify-center w-[55px] h-[36px] rounded-full shrink-0"
          style={{
            background: dark
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(6.3px)",
            WebkitBackdropFilter: "blur(6.3px)",
            boxShadow: dark
              ? `0px 3.66px 3.06px 0px #FFFFFF21 inset, 0px -3.66px 1.83px 0px #0000001A inset, 0px 1px 4px 0px #FFFFFF inset, 0px 4px 4px 0px #00000040`
              : "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
          }}
        >
          <div className="relative w-6 h-6">
            <span className="absolute inset-0 ai-star-1">
              <Image
                src={
                  dark
                    ? Images?.aiButtonStars.star
                    : Images?.aiButtonStars.starDark
                }
                alt=""
                fill
                sizes="24px"
                className="object-contain"
              />
            </span>
            <span className="absolute inset-0 ai-star-2">
              <Image
                src={
                  dark
                    ? Images?.aiButtonStars.star2
                    : Images?.aiButtonStars.starDark2
                }
                alt=""
                fill
                sizes="24px"
                className="object-contain"
              />
            </span>
            <span className="absolute inset-0 ai-star-3">
              <Image
                src={
                  dark
                    ? Images?.aiButtonStars.star3
                    : Images?.aiButtonStars.starDark3
                }
                alt=""
                fill
                sizes="24px"
                className="object-contain"
              />
            </span>
            <span className="absolute inset-0 ai-star-4">
              <Image
                src={
                  dark
                    ? Images?.aiButtonStars.star5
                    : Images?.aiButtonStars.starDark4
                }
                alt=""
                fill
                sizes="24px"
                className="object-contain"
              />
            </span>
          </div>
        </div>
      </div>

      {/* 4. Refined Gradient Border - Removed hardcoded viewBox for fluidity */}
      <svg
        className="pointer-events-none absolute inset-0 w-full h-full z-20"
        aria-hidden="true"
      >
        <defs>
          <mask id="final-border-mask">
            {/* White rect defines the visible part of the mask (the stroke) */}
            <rect
              x="1"
              y="1"
              width="calc(100% - 2px)"
              height="calc(100% - 2px)"
              rx="25"
              fill="none"
              stroke="white"
              strokeWidth="2"
            />
          </mask>
          <linearGradient id="ai-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FF2F4C" />
            <stop offset="53.89%" stopColor="#FCC21B" />
            <stop offset="104.76%" stopColor="#8723C1" />
          </linearGradient>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          rx="26"
          fill="url(#ai-gradient)"
          mask="url(#final-border-mask)"
        />
      </svg>

      <style jsx>{`
        .ai-star-1 {
          animation: starCycle1 4s steps(1) infinite;
        }
        .ai-star-2 {
          animation: starCycle2 4s steps(1) infinite;
        }
        .ai-star-3 {
          animation: starCycle3 4s steps(1) infinite;
        }
        .ai-star-4 {
          animation: starCycle4 4s steps(1) infinite;
        }
      `}</style>
    </button>
  );
}
